import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Box,
    Button,
    Typography,
    Stepper,
    Step,
    StepLabel,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    Close as CloseIcon,
    ArrowBackIos as ArrowBackIcon,
    ArrowForwardIos as ArrowForwardIcon,
} from "@mui/icons-material";

// Import tutorial images directly from assests folder
import NavbarImage from "../assests/tutorial/navbar.jpg";
import ServiceImage from "../assests/tutorial/service.jpg";
import EditServiceImage from "../assests/tutorial/edit-service.png";

const tutorialImages = {
    navbar: NavbarImage,
    service: ServiceImage,
    editService: EditServiceImage,
};

const TutorialModal = ({ open, onClose, onComplete }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [activeStep, setActiveStep] = useState(0);

    const tutorialSteps = [
        {
            label: "Navigation Bar",
            description: "Use the navigation bar to access different sections of the admin panel.",
            image: tutorialImages.navbar,
        },
        {
            label: "Service Management",
            description: "Manage your services from the service management page.",
            image: tutorialImages.service,
        },
        {
            label: "Edit Service",
            description: "Click on a service to edit its details and settings.",
            image: tutorialImages.editService,
        },
    ];

    const handleNext = () => {
        if (activeStep < tutorialSteps.length - 1) {
            setActiveStep(activeStep + 1);
        } else {
            handleComplete();
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        }
    };

    const handleComplete = () => {
        if (onComplete) {
            onComplete();
        }
        onClose();
    };

    const handleSkip = () => {
        if (onComplete) {
            onComplete();
        }
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: "90vh",
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pb: 1,
                }}
            >
                <Typography variant="h6" component="div">
                    Welcome Tutorial
                </Typography>
                <Box>
                    <IconButton
                        onClick={handleSkip}
                        size="small"
                        sx={{ mr: 1 }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Skip
                        </Typography>
                    </IconButton>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {tutorialSteps.map((step, index) => (
                            <Step key={index}>
                                <StepLabel>{step.label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        minHeight: isMobile ? "300px" : "500px",
                        mb: 3,
                    }}
                >
                    <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ mb: 2, textAlign: "center" }}
                    >
                        {tutorialSteps[activeStep].label}
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3, textAlign: "center", maxWidth: "600px" }}
                    >
                        {tutorialSteps[activeStep].description}
                    </Typography>

                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: "800px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: "#f5f5f5",
                            borderRadius: 2,
                            overflow: "hidden",
                            minHeight: isMobile ? "300px" : "400px",
                        }}
                    >
                        <img
                            src={tutorialSteps[activeStep].image}
                            alt={tutorialSteps[activeStep].label}
                            onError={(e) => {
                                console.error('Failed to load image:', tutorialSteps[activeStep].image);
                                e.target.style.display = 'none';
                            }}
                            style={{
                                width: "100%",
                                height: "auto",
                                objectFit: "contain",
                                display: "block",
                            }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 3,
                    }}
                >
                    <Button
                        onClick={handleBack}
                        disabled={activeStep === 0}
                        startIcon={<ArrowBackIcon />}
                        variant="outlined"
                    >
                        Back
                    </Button>

                    <Typography variant="body2" color="text.secondary">
                        {activeStep + 1} of {tutorialSteps.length}
                    </Typography>

                    <Button
                        onClick={handleNext}
                        variant="contained"
                        endIcon={
                            activeStep === tutorialSteps.length - 1 ? null : (
                                <ArrowForwardIcon />
                            )
                        }
                    >
                        {activeStep === tutorialSteps.length - 1
                            ? "Finish"
                            : "Next"}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default TutorialModal;
